import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@wms/shared';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, apiFetch } from '../../lib/api';
import { useCan } from '../../lib/session';
import { getCurrentWarehouseId } from '../../lib/warehouse';
import { LocationTreeSelect } from '../location/location-tree-select';
import type { StockCountListItem, StockCountScopeType } from './types';
import { SkeletonRows } from '../skeleton';

const STATUS_TONE: Record<string, 'default' | 'info' | 'success' | 'danger' | 'warn'> = {
  DRAFT: 'default',
  IN_PROGRESS: 'info',
  PENDING_APPROVAL: 'warn',
  APPROVED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'default',
};

function StatusBadge({ status }: { readonly status: string }): React.JSX.Element {
  const { t } = useTranslation();
  const tone = STATUS_TONE[status] ?? 'default';
  return (
    <span className={tone === 'default' ? 'badge' : `badge badge--${tone}`}>
      {t(`stockCount.status${status}`)}
    </span>
  );
}

function ErrorLine({ error }: { readonly error: unknown }): React.JSX.Element | null {
  const { t } = useTranslation();
  if (!(error instanceof ApiError)) return null;
  return (
    <p className="field-error">
      {t(`errors:${error.code}`, { defaultValue: error.code, ...error.params })}
    </p>
  );
}

/** 盘点列表（S13）。创建表单内嵌为一个简单模态——范围选择 + 显示系统数量开关（U-01）。 */
export function StockCountsListPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canCreate = useCan(PERMISSIONS.STOCKCOUNT_CREATE);
  const [showCreate, setShowCreate] = useState(false);
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  const { data, isPending } = useQuery({
    queryKey: ['stock-counts', 'list'],
    queryFn: () => apiFetch<{ items: StockCountListItem[] }>('/stock-counts'),
  });

  return (
    <div>
      <div className="row row--between">
        <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('nav.stockCount')}</h1>
        {canCreate && (
          <button type="button" className="btn btn--primary" onClick={() => setShowCreate(true)}>
            {t('stockCount.newCount')}
          </button>
        )}
      </div>

      {isPending ? (
        <SkeletonRows />
      ) : (data?.items.length ?? 0) === 0 ? (
        <div className="empty-state">
          <p>{t('stockCount.empty')}</p>
        </div>
      ) : (
        <div className="table" style={{ display: 'block' }}>
          <div
            className="row"
            style={{
              fontWeight: 600,
              padding: 'var(--space-2) 0',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <span style={{ width: '160px' }}>{t('stockCount.colDoc')}</span>
            <span style={{ width: '140px' }}>{t('stockCount.colScope')}</span>
            <span style={{ width: '160px' }}>{t('stockCount.colStatus')}</span>
            <span style={{ width: '160px' }}>{t('stockCount.colProgress')}</span>
            <span style={{ width: '100px' }}>{t('stockCount.colDiff')}</span>
            <span style={{ width: '160px' }}>{t('stockCount.colCreatedAt')}</span>
          </div>
          {data!.items.map((c) => (
            <Link
              key={c.id}
              to={`/stock-counts/${c.id}`}
              className="tree__row"
              style={{ height: '48px', textDecoration: 'none', color: 'inherit' }}
            >
              <span style={{ width: '160px' }}>{c.docNo}</span>
              <span style={{ width: '140px' }}>{t(`stockCount.scope${c.scopeType}`)}</span>
              <span style={{ width: '160px' }}>
                <StatusBadge status={c.status} />
              </span>
              <span style={{ width: '160px' }}>
                {t('stockCount.progressFraction', { counted: c.countedLineCount, total: c.lineCount })}
              </span>
              <span style={{ width: '100px' }}>{c.diffLineCount}</span>
              <span style={{ width: '160px' }}>{fmt.format(new Date(c.createdAt))}</span>
            </Link>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateCountModal
          onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            setShowCreate(false);
            void queryClient.invalidateQueries({ queryKey: ['stock-counts'] });
            navigate(`/stock-counts/${id}`);
          }}
        />
      )}
    </div>
  );
}

function CreateCountModal({
  onClose,
  onCreated,
}: {
  readonly onClose: () => void;
  readonly onCreated: (id: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [scopeType, setScopeType] = useState<StockCountScopeType>('WAREHOUSE');
  const [scopeLocationId, setScopeLocationId] = useState<string | null>(null);
  const [showSystemQty, setShowSystemQty] = useState(false);

  const createMutation = useMutation({
    mutationFn: () =>
      apiFetch<{ id: string }>('/stock-counts', {
        method: 'POST',
        body: {
          scopeType,
          scopeLocationId: scopeType === 'WAREHOUSE' ? undefined : (scopeLocationId ?? undefined),
          showSystemQty,
        },
      }),
    onSuccess: (res) => onCreated(res.id),
  });

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h2>{t('stockCount.newCount')}</h2>
        <label className="field">
          <span>{t('stockCount.scopeLabel')}</span>
          <select value={scopeType} onChange={(e) => setScopeType(e.target.value as StockCountScopeType)}>
            <option value="WAREHOUSE">{t('stockCount.scopeWAREHOUSE')}</option>
            <option value="LOCATION_SUBTREE">{t('stockCount.scopeLOCATION_SUBTREE')}</option>
            <option value="LOCATION">{t('stockCount.scopeLOCATION')}</option>
          </select>
        </label>
        {scopeType !== 'WAREHOUSE' && (
          <label className="field">
            <span>{t('stockCount.scopeLocationLabel')}</span>
            <LocationTreeSelect
              warehouseId={getCurrentWarehouseId()!}
              value={scopeLocationId}
              onChange={setScopeLocationId}
            />
          </label>
        )}
        <label className="row" style={{ gap: 'var(--space-2)', margin: 'var(--space-2) 0' }}>
          <input
            type="checkbox"
            checked={showSystemQty}
            onChange={(e) => setShowSystemQty(e.target.checked)}
          />
          <span>{t('stockCount.showSystemQtyToggle')}</span>
        </label>
        <p className="field-hint">{t('stockCount.showSystemQtyHint')}</p>
        <ErrorLine error={createMutation.error} />
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('app.cancel')}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={createMutation.isPending || (scopeType !== 'WAREHOUSE' && !scopeLocationId)}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? t('app.loading') : t('stockCount.createSubmit')}
          </button>
        </div>
      </div>
    </div>
  );
}
